import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Sidebar } from '../components/Sidebar';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { PageHeader, StatusBadge, LoadingSpinner, EmptyState } from '../components/UIComponents';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { sitesAPI, dataAPI, assetsAPI, siteMeteringAPI, clientsAPI, jemAPI } from '../utils/api';
import { formatDecimal, formatDateTime } from '../utils/helpers';
import { Plus, Building2, MapPin, Zap, RefreshCw, Battery, ChevronRight, Users, Link, Unlink, Plug, CheckCircle, XCircle, Clock, MoreVertical, Archive, ArchiveRestore, Trash2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { CreateSiteModal } from '../components/CreateSiteModal';
import { CreateAssetModal } from '../components/CreateAssetModal';
import LiveMonitoringTab from '../components/LiveMonitoringTab';
import SiteDiagnosticsTab from '../components/SiteDiagnosticsTab';
import ReportsTab from '../components/ReportsTab';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

export const SitesPage = () => {
  const [sites, setSites] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateSite, setShowCreateSite] = useState(false);
  const [clientFilter, setClientFilter] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [sitesRes, clientsRes] = await Promise.all([
        sitesAPI.getAll({ include_archived: showArchived }),
        clientsAPI.getAll(),
      ]);
      setSites(sitesRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      console.error('Failed to fetch sites:', error);
      toast.error('Failed to load sites');
    } finally {
      setLoading(false);
    }
  }, [showArchived]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const clientMap = clients.reduce((m, c) => { m[c.client_id] = c.name; return m; }, {});
  const filtered = clientFilter ? sites.filter(s => s.client_id === clientFilter) : sites;

  if (loading) {
    return (
      <div className="flex">
        <Sidebar />
        <div className="flex-1 ml-0 lg:ml-64 pt-14 lg:pt-0">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50 overflow-x-hidden">
      <Sidebar />
      <div className="flex-1 ml-0 lg:ml-64 pt-14 lg:pt-0 overflow-x-hidden">
        <div className="p-4 sm:p-8">
          <PageHeader
            title="Sites"
            description="Manage and monitor all BESS sites"
            action={
              <div className="flex items-center space-x-2">
                {isAdmin && (
                  <label
                    className="flex items-center gap-1.5 text-xs text-slate-600 border border-slate-200 rounded px-2.5 py-1.5 cursor-pointer hover:bg-slate-50"
                    data-testid="toggle-show-archived"
                  >
                    <input
                      type="checkbox"
                      checked={showArchived}
                      onChange={(e) => setShowArchived(e.target.checked)}
                      className="w-3.5 h-3.5"
                    />
                    Show archived
                  </label>
                )}
                {clients.length > 0 && (
                  <select
                    value={clientFilter}
                    onChange={e => setClientFilter(e.target.value)}
                    className="text-sm border border-slate-200 rounded px-2.5 py-1.5 text-slate-600"
                    data-testid="client-filter"
                  >
                    <option value="">All Clients</option>
                    {clients.map(c => (
                      <option key={c.client_id} value={c.client_id}>{c.name}</option>
                    ))}
                  </select>
                )}
                {isAdmin && (
                  <Button
                    onClick={() => setShowCreateSite(true)}
                    data-testid="create-site-button"
                    className="bg-slate-900 hover:bg-slate-800"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Site
                  </Button>
                )}
              </div>
            }
          />

          {filtered.length === 0 ? (
            <EmptyState
              icon={Building2}
              title="No sites found"
              description={clientFilter ? "No sites for this client" : "Get started by adding your first BESS site"}
              action={
                isAdmin && (
                  <Button onClick={() => setShowCreateSite(true)} className="bg-slate-900 hover:bg-slate-800">
                    <Plus className="w-4 h-4 mr-2" />
                    Create Site
                  </Button>
                )
              }
            />
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {filtered.map((site) => (
                <div
                  key={site.site_id}
                  onClick={() => navigate(`/sites/${site.site_id}`)}
                  data-testid={`site-card-${site.site_id}`}
                  className="bg-white border border-slate-200 rounded-sm p-6 shadow-sm hover:shadow-md transition-all cursor-pointer"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-slate-100 rounded-sm flex items-center justify-center">
                        <Building2 className="w-6 h-6 text-slate-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{site.name}</h3>
                        <p className="text-sm text-slate-600 flex items-center mt-1">
                          <MapPin className="w-3 h-3 mr-1" />
                          {site.location}
                        </p>
                        {clientMap[site.client_id] && (
                          <p className="text-xs text-slate-400 flex items-center mt-0.5">
                            <Users className="w-3 h-3 mr-1" />
                            {clientMap[site.client_id]}
                          </p>
                        )}
                      </div>
                    </div>
                    <StatusBadge status={site.live_status || 'offline'} />
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Power</p>
                      <p className="text-lg font-mono font-medium text-slate-900">
                        {formatDecimal(site.installed_power_kw, 0)} kW
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Capacity</p>
                      <p className="text-lg font-mono font-medium text-slate-900">
                        {formatDecimal(site.installed_capacity_kwh, 0)} kWh
                      </p>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-slate-100">
                    <p className="text-xs text-slate-500">
                      Last data: {site.last_data_at ? formatDateTime(site.last_data_at) : 'Never'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <CreateSiteModal
        open={showCreateSite}
        onClose={() => setShowCreateSite(false)}
        onCreated={() => fetchData()}
      />
    </div>
  );
};

export const SiteDetailPage = () => {
  const { siteId } = useParams();
  const navigate = useNavigate();
  const [siteDetail, setSiteDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generatingData, setGeneratingData] = useState(false);
  const [activeTab, setActiveTab] = useState('live');
  const [showCreateAsset, setShowCreateAsset] = useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [siteActionInFlight, setSiteActionInFlight] = useState(false);
  // Asset lifecycle
  const [assetsList, setAssetsList] = useState([]);
  const [showArchivedAssets, setShowArchivedAssets] = useState(false);
  const [assetAction, setAssetAction] = useState(null); // {type:'archive'|'restore'|'delete', asset}
  const [assetActionInFlight, setAssetActionInFlight] = useState(false);
  const { isAdmin } = useAuth();

  // JEM tab state
  const [jemStations, setJemStations] = useState([]);
  const [jemSystems, setJemSystems] = useState({});
  const [jemMappings, setJemMappings] = useState([]);
  const [jemAuthStatus, setJemAuthStatus] = useState(null);
  const [jemTelemetry, setJemTelemetry] = useState({});
  const [jemSyncing, setJemSyncing] = useState(false);
  const [jemPolling, setJemPolling] = useState({});

  const fetchSiteDetails = useCallback(async () => {
    try {
      const detailRes = await siteMeteringAPI.getSiteDetail(siteId);
      setSiteDetail(detailRes.data);
    } catch (error) {
      console.error('Failed to fetch site details:', error);
      toast.error('Failed to load site details');
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  const fetchJemData = useCallback(async () => {
    try {
      const [authRes, stationsRes, mappingsRes] = await Promise.all([
        jemAPI.authStatus(),
        jemAPI.getStations(),
        jemAPI.getSiteMappings(siteId),
      ]);
      setJemAuthStatus(authRes.data);
      setJemStations(stationsRes.data);
      setJemMappings(mappingsRes.data);
      // Fetch latest telemetry for each mapping
      const telemetryMap = {};
      for (const m of mappingsRes.data) {
        try {
          const res = await jemAPI.getLatestTelemetry(m.asset_id);
          telemetryMap[m.asset_id] = res.data;
        } catch (e) { console.error('JEM telemetry fetch failed for asset', m.asset_id, e); }
      }
      setJemTelemetry(telemetryMap);
    } catch (e) { console.debug('JEM not configured for this site', e); }
  }, [siteId]);

  useEffect(() => {
    fetchSiteDetails();
  }, [fetchSiteDetails]);

  useEffect(() => {
    if (activeTab === 'diagnostics' && siteDetail) fetchJemData();
  }, [activeTab, siteDetail, fetchJemData]);

  const handleGenerateMockData = async () => {
    setGeneratingData(true);
    try {
      await dataAPI.createMockTelemetry(siteId);
      toast.success('Mock telemetry data generated');
      await fetchSiteDetails();
    } catch (error) {
      toast.error('Failed to generate data');
    } finally {
      setGeneratingData(false);
    }
  };

  const handleArchiveSite = async () => {
    setSiteActionInFlight(true);
    try {
      await sitesAPI.archive(siteId);
      toast.success('Site archived');
      setArchiveDialogOpen(false);
      navigate('/sites');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to archive site');
    } finally { setSiteActionInFlight(false); }
  };

  const handleRestoreSite = async () => {
    setSiteActionInFlight(true);
    try {
      await sitesAPI.restore(siteId);
      toast.success('Site restored');
      setRestoreDialogOpen(false);
      await fetchSiteDetails();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to restore site');
    } finally { setSiteActionInFlight(false); }
  };

  const handleDeleteSite = async () => {
    setSiteActionInFlight(true);
    try {
      const res = await sitesAPI.delete(siteId);
      toast.success(
        `Site deleted · ${res.data?.assets_deleted || 0} asset(s) removed`,
      );
      setDeleteDialogOpen(false);
      navigate('/sites');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete site');
    } finally { setSiteActionInFlight(false); }
  };

  // ── Asset lifecycle ────────────────────────────────────────
  const fetchAssetsList = useCallback(async () => {
    try {
      const r = await assetsAPI.getAll(siteId, { include_archived: showArchivedAssets });
      setAssetsList(r.data || []);
    } catch (e) {
      // Silent — fall back to site.assets on the initial render
      console.error('fetchAssetsList failed', e);
    }
  }, [siteId, showArchivedAssets]);

  useEffect(() => {
    if (activeTab === 'assets') fetchAssetsList();
  }, [activeTab, showArchivedAssets, fetchAssetsList]);

  const handleAssetArchive = async () => {
    if (!assetAction?.asset) return;
    setAssetActionInFlight(true);
    try {
      await assetsAPI.archive(assetAction.asset.asset_id);
      toast.success(`Asset "${assetAction.asset.asset_name}" archived`);
      setAssetAction(null);
      await fetchAssetsList();
      await fetchSiteDetails();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to archive asset');
    } finally { setAssetActionInFlight(false); }
  };

  const handleAssetRestore = async () => {
    if (!assetAction?.asset) return;
    setAssetActionInFlight(true);
    try {
      await assetsAPI.restore(assetAction.asset.asset_id);
      toast.success(`Asset "${assetAction.asset.asset_name}" restored`);
      setAssetAction(null);
      await fetchAssetsList();
      await fetchSiteDetails();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to restore asset');
    } finally { setAssetActionInFlight(false); }
  };

  const handleAssetDelete = async () => {
    if (!assetAction?.asset) return;
    setAssetActionInFlight(true);
    try {
      await assetsAPI.delete(assetAction.asset.asset_id);
      toast.success(`Asset "${assetAction.asset.asset_name}" deleted permanently`);
      setAssetAction(null);
      await fetchAssetsList();
      await fetchSiteDetails();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete asset');
    } finally { setAssetActionInFlight(false); }
  };

  // JEM handlers
  const handleLinkStation = async (station) => {
    try {
      await jemAPI.linkStation(siteId, {
        station_id: station.station_id,
        station_name: station.station_name,
        timezone: station.timezone || '',
        offset: station.offset || '',
      });
      toast.success('Station linked to site');
      await fetchSiteDetails();
      await fetchJemData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to link station'); }
  };

  const handleUnlinkStation = async () => {
    if (!window.confirm('Remove JEM station link from this site?')) return;
    try {
      await jemAPI.unlinkStation(siteId);
      toast.success('Station unlinked');
      await fetchSiteDetails();
      await fetchJemData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleSyncSystems = async (stationId) => {
    setJemSyncing(true);
    try {
      const res = await jemAPI.syncSystems(stationId);
      toast.success(`Synced ${res.data.count} systems`);
      const cached = await jemAPI.getSystems(stationId);
      setJemSystems(prev => ({ ...prev, [stationId]: cached.data }));
    } catch (e) { toast.error(e.response?.data?.detail || 'Sync failed'); }
    finally { setJemSyncing(false); }
  };

  const handleMapSystemToAsset = async (systemData, assetId) => {
    try {
      const linkedStation = siteDetail?.vendor_integrations?.jem_portal;
      await jemAPI.createMapping({
        station_id: systemData.station_id,
        system_id: systemData.system_id,
        ems_device_id: systemData.ems_device_id || '',
        site_id: siteId,
        asset_id: assetId,
        site_name: siteDetail?.name || '',
      });
      toast.success('System mapped to asset');
      await fetchJemData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Mapping failed'); }
  };

  const handleCreateAssetFromSystem = async (systemData) => {
    try {
      await jemAPI.createAssetFromSystem({
        site_id: siteId,
        station_id: systemData.station_id,
        system_id: systemData.system_id,
        ems_device_id: systemData.ems_device_id || '',
        ems_sn: systemData.ems_sn || '',
        rated_power: systemData.rated_power,
        nominal_energy_cap: systemData.nominal_energy_cap,
        name: systemData.name || `JEM ${systemData.ems_device_id || systemData.system_id}`,
      });
      toast.success('Asset created & mapped');
      await fetchSiteDetails();
      await fetchJemData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleDeleteMapping = async (mappingId) => {
    if (!window.confirm('Remove this JEM mapping?')) return;
    try {
      await jemAPI.deleteMapping(mappingId);
      toast.success('Mapping removed');
      await fetchJemData();
    } catch { toast.error('Failed'); }
  };

  const handleSyncNow = async (assetId) => {
    setJemPolling(prev => ({ ...prev, [assetId]: true }));
    try {
      const res = await jemAPI.pollAsset(assetId);
      if (res.data.status === 'success') toast.success('Telemetry synced');
      else toast.warning(res.data.message || 'No data returned');
      await fetchJemData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Sync failed'); }
    finally { setJemPolling(prev => ({ ...prev, [assetId]: false })); }
  };

  const handleSyncAll = async () => {
    const station = siteDetail?.vendor_integrations?.jem_portal;
    if (!station) { toast.error('No JEM station linked'); return; }
    setJemSyncing(true);
    try {
      // Step 1: Sync systems
      const sysRes = await jemAPI.syncSystems(station.station_id);
      const cached = await jemAPI.getSystems(station.station_id);
      setJemSystems(prev => ({ ...prev, [station.station_id]: cached.data }));

      // Step 2: Refresh mappings
      const mappingsRes = await jemAPI.getSiteMappings(siteId);
      setJemMappings(mappingsRes.data);

      // Step 3: Poll all mapped assets
      let pollCount = 0;
      for (const m of mappingsRes.data) {
        try {
          const r = await jemAPI.pollAsset(m.asset_id);
          if (r.data.status === 'success') pollCount++;
        } catch (e) { console.error('JEM poll failed for asset', m.asset_id, e); }
      }
      await fetchJemData();
      toast.success(`Synced ${sysRes.data.count} systems, polled ${pollCount}/${mappingsRes.data.length} assets`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Sync all failed'); }
    finally { setJemSyncing(false); }
  };

  if (loading) {
    return (
      <div className="flex"><Sidebar /><div className="flex-1 ml-0 lg:ml-64 pt-14 lg:pt-0"><LoadingSpinner /></div></div>
    );
  }

  if (!siteDetail) {
    return (
      <div className="flex"><Sidebar /><div className="flex-1 ml-0 lg:ml-64 pt-14 lg:pt-0 p-8">
        <EmptyState icon={Building2} title="Site not found" description="The requested site does not exist" />
      </div></div>
    );
  }

  const site = siteDetail;
  const assets = assetsList.length > 0 ? assetsList : (site.assets || []);
  const linkedStation = site.vendor_integrations?.jem_portal;

  const breadcrumbs = [
    { label: 'Fleet', to: '/dashboard' },
    { label: site.client_name || 'Client', to: '/clients' },
    { label: site.name },
  ];

  const tabs = [
    { key: 'live', label: 'Live Monitoring' },
    { key: 'assets', label: `Assets (${assets.length})` },
    { key: 'reports', label: 'Reports' },
    { key: 'diagnostics', label: 'Diagnostics' },
  ];

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      <div className="flex-1 ml-0 lg:ml-64 pt-14 lg:pt-0">
        <div className="p-6 lg:p-8">
          <Breadcrumbs items={breadcrumbs} onBack={() => navigate('/sites')} />

          {/* Archived banner */}
          {site.archived && (
            <div
              className="bg-amber-50 border border-amber-200 rounded-sm p-4 mb-4 flex items-center justify-between"
              data-testid="archived-banner"
            >
              <div className="flex items-center gap-3">
                <Archive className="w-5 h-5 text-amber-600" />
                <div>
                  <div className="text-sm font-semibold text-amber-900">This site is archived</div>
                  <div className="text-xs text-amber-800">
                    Hidden from the Fleet list. Historical data preserved.
                    {site.archived_at && ` Archived on ${formatDateTime(site.archived_at)}`}
                    {site.archived_by && ` by ${site.archived_by}`}
                  </div>
                </div>
              </div>
              {isAdmin && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setRestoreDialogOpen(true)}
                  data-testid="restore-site-btn"
                >
                  <ArchiveRestore className="w-4 h-4 mr-2" /> Restore
                </Button>
              )}
            </div>
          )}

          {/* Site Header — identity + admin actions only. Live status, specs and
              last-data timestamp live in the Live Monitoring status band (single source). */}
          <div className="bg-white border border-slate-200 rounded-sm p-6 shadow-sm mb-6" data-testid="site-header">
            <div className="flex items-start justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-14 h-14 bg-slate-100 rounded-sm flex items-center justify-center">
                  <Building2 className="w-7 h-7 text-slate-600" />
                </div>
                <div>
                  <h1 className="text-xl lg:text-2xl font-bold text-slate-900 mb-1">{site.name}</h1>
                  <p className="text-slate-600 flex items-center text-sm">
                    <MapPin className="w-4 h-4 mr-1" />
                    {site.location}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                {isAdmin && (
                  <Button onClick={handleGenerateMockData} disabled={generatingData} variant="outline" size="sm">
                    <RefreshCw className={`w-4 h-4 mr-2 ${generatingData ? 'animate-spin' : ''}`} />
                    {generatingData ? 'Generating...' : 'Generate Data'}
                  </Button>
                )}
                {isAdmin && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" data-testid="site-actions-menu-trigger">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-52">
                      {site.archived ? (
                        <DropdownMenuItem
                          onClick={() => setRestoreDialogOpen(true)}
                          data-testid="site-menu-restore"
                        >
                          <ArchiveRestore className="w-4 h-4 mr-2" /> Restore site
                        </DropdownMenuItem>
                      ) : (
                        <DropdownMenuItem
                          onClick={() => setArchiveDialogOpen(true)}
                          data-testid="site-menu-archive"
                        >
                          <Archive className="w-4 h-4 mr-2" /> Archive site
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => setDeleteDialogOpen(true)}
                        className="text-rose-600 focus:text-rose-700 focus:bg-rose-50"
                        data-testid="site-menu-delete"
                      >
                        <Trash2 className="w-4 h-4 mr-2" /> Delete site permanently
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>

          </div>

          {/* Tab Navigation */}
          <div className="flex border-b border-slate-200 mb-6">
            {tabs.map(t => (
              <button key={t.key} onClick={() => setActiveTab(t.key)}
                className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === t.key ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
                data-testid={`site-tab-${t.key}`}>
                {t.label}
              </button>
            ))}
          </div>

          {/* TAB: Live Monitoring — consolidated single-source page */}
          {activeTab === 'live' && (
            <LiveMonitoringTab siteId={siteId} />
          )}

          {/* TAB: Assets */}
          {activeTab === 'assets' && (
            <div className="bg-white border border-slate-200 rounded-sm shadow-sm" data-testid="assets-list">
              <div className="p-5 border-b border-slate-200 flex items-center justify-between">
                <h3 className="font-semibold text-slate-900 flex items-center">
                  <Battery className="w-5 h-5 mr-2 text-blue-600" />
                  BESS Assets ({assets.length})
                </h3>
                <div className="flex items-center space-x-2">
                  {isAdmin && (
                    <label
                      className="flex items-center gap-1.5 text-xs text-slate-600 border border-slate-200 rounded px-2.5 py-1.5 cursor-pointer hover:bg-slate-50"
                      data-testid="toggle-show-archived-assets"
                    >
                      <input
                        type="checkbox"
                        checked={showArchivedAssets}
                        onChange={(e) => setShowArchivedAssets(e.target.checked)}
                        className="w-3.5 h-3.5"
                      />
                      Show archived
                    </label>
                  )}
                  {isAdmin && (
                    <Button size="sm" className="bg-slate-900 hover:bg-slate-800" onClick={() => setShowCreateAsset(true)} data-testid="create-asset-btn">
                      <Plus className="w-4 h-4 mr-2" /> Create Asset
                    </Button>
                  )}
                </div>
              </div>
              {assets.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  <Battery className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                  <p className="text-sm">
                    {showArchivedAssets ? 'No assets (archived or active) for this site' : 'No active assets for this site'}
                  </p>
                  {isAdmin && (
                    <Button size="sm" className="mt-3 bg-slate-900" onClick={() => setShowCreateAsset(true)}>
                      <Plus className="w-4 h-4 mr-2" /> Create Asset
                    </Button>
                  )}
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {assets.map((asset) => (
                    <div
                      key={asset.asset_id}
                      data-testid={`asset-row-${asset.asset_id}`}
                      className={`p-4 hover:bg-slate-50 transition-colors flex items-center justify-between ${asset.archived ? 'opacity-70' : ''}`}
                    >
                      <div
                        className="flex items-center space-x-4 flex-1 min-w-0 cursor-pointer"
                        onClick={() => navigate(`/assets/${asset.asset_id}`)}
                      >
                        <div className="w-10 h-10 bg-blue-50 border border-blue-200 rounded-sm flex items-center justify-center flex-shrink-0">
                          <Battery className="w-5 h-5 text-blue-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-slate-900 text-sm truncate">{asset.asset_name}</p>
                            {asset.archived && (
                              <span
                                className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200"
                                data-testid={`archived-pill-asset-${asset.asset_id}`}
                              >
                                Archived
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-slate-500">
                            {formatDecimal(asset.installed_power_kw, 0)} kW / {formatDecimal(asset.installed_capacity_kwh, 0)} kWh
                            {asset.manufacturer && ` · ${asset.manufacturer}`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-6 flex-shrink-0">
                        <StatusBadge status={asset.status} />
                        {isAdmin && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => e.stopPropagation()}
                                data-testid={`asset-actions-trigger-${asset.asset_id}`}
                              >
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-52">
                              {asset.archived ? (
                                <DropdownMenuItem
                                  onClick={() => setAssetAction({ type: 'restore', asset })}
                                  data-testid={`asset-menu-restore-${asset.asset_id}`}
                                >
                                  <ArchiveRestore className="w-4 h-4 mr-2" /> Restore asset
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem
                                  onClick={() => setAssetAction({ type: 'archive', asset })}
                                  data-testid={`asset-menu-archive-${asset.asset_id}`}
                                >
                                  <Archive className="w-4 h-4 mr-2" /> Archive asset
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => setAssetAction({ type: 'delete', asset })}
                                className="text-rose-600 focus:text-rose-700 focus:bg-rose-50"
                                data-testid={`asset-menu-delete-${asset.asset_id}`}
                              >
                                <Trash2 className="w-4 h-4 mr-2" /> Delete asset permanently
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                        <ChevronRight
                          className="w-4 h-4 text-slate-400 cursor-pointer"
                          onClick={() => navigate(`/assets/${asset.asset_id}`)}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* TAB: Diagnostics — device comms/payload + ingestion config (admin) */}
          {activeTab === 'diagnostics' && (
            <div className="space-y-6">
            <SiteDiagnosticsTab siteId={siteId} isAdmin={isAdmin} />
            <JEMPortalTab
              site={site}
              siteId={siteId}
              assets={assets}
              linkedStation={linkedStation}
              jemStations={jemStations}
              jemSystems={jemSystems}
              jemMappings={jemMappings}
              jemAuthStatus={jemAuthStatus}
              jemTelemetry={jemTelemetry}
              jemSyncing={jemSyncing}
              jemPolling={jemPolling}
              onLinkStation={handleLinkStation}
              onUnlinkStation={handleUnlinkStation}
              onSyncSystems={handleSyncSystems}
              onMapSystem={handleMapSystemToAsset}
              onCreateAssetFromSystem={handleCreateAssetFromSystem}
              onDeleteMapping={handleDeleteMapping}
              onSyncNow={handleSyncNow}
              onSyncAll={handleSyncAll}
              isAdmin={isAdmin}
            />
            </div>
          )}

          {/* TAB: Reports */}
          {activeTab === 'reports' && (
            <ReportsTab siteId={siteId} siteName={site.name} onSiteUpdated={fetchSiteDetails} />
          )}
        </div>
      </div>

      <CreateAssetModal
        open={showCreateAsset}
        onClose={() => setShowCreateAsset(false)}
        onCreated={() => fetchSiteDetails()}
        siteId={siteId}
      />

      {/* Archive confirm */}
      <AlertDialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
        <AlertDialogContent data-testid="archive-site-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Archive this site?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{site?.name}</strong> will be hidden from the Fleet list and excluded
              from live monitoring. All historical data (telemetry, outages, reports) stays
              intact and the site can be restored at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="archive-cancel-btn" disabled={siteActionInFlight}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleArchiveSite}
              disabled={siteActionInFlight}
              data-testid="archive-confirm-btn"
            >
              {siteActionInFlight ? 'Archiving…' : 'Archive site'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Restore confirm */}
      <AlertDialog open={restoreDialogOpen} onOpenChange={setRestoreDialogOpen}>
        <AlertDialogContent data-testid="restore-site-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Restore this site?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{site?.name}</strong> will be brought back into the Fleet list and
              live monitoring resumes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="restore-cancel-btn" disabled={siteActionInFlight}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRestoreSite}
              disabled={siteActionInFlight}
              data-testid="restore-confirm-btn"
            >
              {siteActionInFlight ? 'Restoring…' : 'Restore site'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirm */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent data-testid="delete-site-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-rose-700">
              Delete this site permanently?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will <strong>permanently remove</strong> <em>{site?.name}</em> and its BESS assets.
              <br /><br />
              Historical outage events, alarms, telemetry and JEM mappings are kept for audit
              but marked as orphaned. This action <strong>cannot be undone</strong>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn" disabled={siteActionInFlight}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteSite}
              disabled={siteActionInFlight}
              className="bg-rose-600 hover:bg-rose-700 focus:ring-rose-600"
              data-testid="delete-confirm-btn"
            >
              {siteActionInFlight ? 'Deleting…' : 'Yes, delete permanently'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Asset lifecycle confirm — archive/restore/delete */}
      <AlertDialog
        open={!!assetAction}
        onOpenChange={(open) => { if (!open) setAssetAction(null); }}
      >
        <AlertDialogContent data-testid="asset-action-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className={assetAction?.type === 'delete' ? 'text-rose-700' : ''}>
              {assetAction?.type === 'archive' && 'Archive this asset?'}
              {assetAction?.type === 'restore' && 'Restore this asset?'}
              {assetAction?.type === 'delete' && 'Delete asset permanently?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {assetAction?.type === 'archive' && (
                <>
                  <strong>{assetAction?.asset?.asset_name}</strong> will be hidden from the
                  active assets list. All telemetry and history stays intact. You can restore it any time.
                </>
              )}
              {assetAction?.type === 'restore' && (
                <>
                  <strong>{assetAction?.asset?.asset_name}</strong> will be brought back to
                  the active asset list.
                </>
              )}
              {assetAction?.type === 'delete' && (
                <>
                  This will <strong>permanently remove</strong> <em>{assetAction?.asset?.asset_name}</em>.
                  <br /><br />
                  Historical telemetry, alarms and JEM mappings are kept for audit but marked as orphaned.
                  <br />
                  <span className="text-rose-600 font-medium">This action cannot be undone.</span>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="asset-action-cancel-btn"
              disabled={assetActionInFlight}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (assetAction?.type === 'archive') handleAssetArchive();
                else if (assetAction?.type === 'restore') handleAssetRestore();
                else if (assetAction?.type === 'delete') handleAssetDelete();
              }}
              disabled={assetActionInFlight}
              className={assetAction?.type === 'delete' ? 'bg-rose-600 hover:bg-rose-700 focus:ring-rose-600' : ''}
              data-testid="asset-action-confirm-btn"
            >
              {assetActionInFlight
                ? 'Working…'
                : assetAction?.type === 'archive'
                  ? 'Archive asset'
                  : assetAction?.type === 'restore'
                    ? 'Restore asset'
                    : 'Yes, delete permanently'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};


function JEMPortalTab({ site, siteId, assets, linkedStation, jemStations, jemSystems, jemMappings, jemAuthStatus, jemTelemetry, jemSyncing, jemPolling, onLinkStation, onUnlinkStation, onSyncSystems, onMapSystem, onCreateAssetFromSystem, onDeleteMapping, onSyncNow, onSyncAll, isAdmin }) {
  const [selectedStation, setSelectedStation] = useState('');
  const [selectedAssetForSystem, setSelectedAssetForSystem] = useState({});

  const stationId = linkedStation?.station_id;
  const systemsList = stationId ? (jemSystems[stationId] || []) : [];

  return (
    <div className="space-y-6" data-testid="jem-portal-tab">
      {/* Auth Status + Sync All */}
      <div className="bg-white border border-slate-200 rounded-sm p-4 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className={`w-3 h-3 rounded-full ${jemAuthStatus?.has_token ? 'bg-green-500' : 'bg-red-500'}`} />
          <span className="text-sm text-slate-700">
            {jemAuthStatus?.has_token ? 'JEM Authenticated' : 'JEM Not Authenticated — Set token in JEM Integration page'}
          </span>
        </div>
        <div className="flex items-center space-x-2">
          {jemAuthStatus?.polling_active && (
            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Polling Active</span>
          )}
          {linkedStation && (
            <Button size="sm" className="bg-slate-900 hover:bg-slate-800" onClick={onSyncAll} disabled={jemSyncing} data-testid="jem-sync-all-btn">
              <RefreshCw className={`w-3 h-3 mr-1 ${jemSyncing ? 'animate-spin' : ''}`} />
              {jemSyncing ? 'Syncing...' : 'Sync All'}
            </Button>
          )}
        </div>
      </div>

      {/* Station Link Section */}
      <div className="bg-white border border-slate-200 rounded-sm p-6">
        <h3 className="font-semibold text-slate-900 mb-4 flex items-center">
          <Plug className="w-5 h-5 mr-2 text-slate-500" /> Linked JEM Station
        </h3>

        {linkedStation ? (
          <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded p-4">
            <div>
              <p className="font-medium text-green-900">{linkedStation.station_name || `Station ${linkedStation.station_id}`}</p>
              <p className="text-xs text-green-700">
                Station ID: {linkedStation.station_id}
                {linkedStation.timezone && ` | TZ: ${linkedStation.timezone}`}
                {linkedStation.last_station_sync_at && ` | Synced: ${new Date(linkedStation.last_station_sync_at).toLocaleDateString()}`}
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <Button size="sm" variant="outline" onClick={() => onSyncSystems(linkedStation.station_id)} disabled={jemSyncing} data-testid="jem-sync-systems-btn">
                <RefreshCw className={`w-3 h-3 mr-1 ${jemSyncing ? 'animate-spin' : ''}`} /> Sync Systems
              </Button>
              {isAdmin && (
                <Button size="sm" variant="outline" className="text-red-500 hover:text-red-700" onClick={onUnlinkStation} data-testid="jem-unlink-station-btn">
                  <Unlink className="w-3 h-3 mr-1" /> Unlink
                </Button>
              )}
            </div>
          </div>
        ) : (
          <div>
            <p className="text-sm text-slate-500 mb-3">No station linked. Select a JEM station to link to this site.</p>
            {jemStations.length === 0 ? (
              <p className="text-xs text-slate-400">No stations available. Sync stations from the JEM Integration admin page first.</p>
            ) : (
              <div className="flex items-end gap-3">
                <div className="flex-1">
                  <Label className="text-xs">Select Station</Label>
                  <select value={selectedStation} onChange={e => setSelectedStation(e.target.value)}
                    className="w-full mt-1 px-3 py-2 border border-slate-200 rounded text-sm" data-testid="jem-station-select">
                    <option value="">Choose a station...</option>
                    {jemStations.map(s => (
                      <option key={s.station_id} value={s.station_id}>{s.station_name} (ID: {s.station_id})</option>
                    ))}
                  </select>
                </div>
                <Button size="sm" className="bg-slate-900" disabled={!selectedStation}
                  onClick={() => {
                    const st = jemStations.find(s => String(s.station_id) === String(selectedStation));
                    if (st) onLinkStation(st);
                  }}
                  data-testid="jem-link-station-btn">
                  <Link className="w-3 h-3 mr-1" /> Link Station
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* System → Asset Mapping */}
      {linkedStation && (
        <div className="bg-white border border-slate-200 rounded-sm p-6">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center">
            <Battery className="w-5 h-5 mr-2 text-blue-600" /> System → Asset Mapping
          </h3>

          {systemsList.length === 0 ? (
            <div className="text-center py-8">
              <Battery className="w-10 h-10 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">No systems found. Click "Sync Systems" above to discover JEM systems.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {systemsList.map(sys => {
                const sysId = sys.system_id || sys.id;
                const existingMapping = jemMappings.find(m => m.system_id === sysId);
                const assetSelect = selectedAssetForSystem[sysId] || '';
                return (
                  <div key={sysId} className={`p-4 rounded border ${existingMapping ? 'border-green-200 bg-green-50' : 'border-slate-100 bg-slate-50'}`} data-testid={`jem-system-${sysId}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-900">{sys.name || `System ${sysId}`}</p>
                        <p className="text-xs text-slate-500">
                          System ID: {sysId} | EMS: {sys.ems_device_id || 'N/A'}
                          {sys.rated_power ? ` | ${sys.rated_power}kW` : ''}
                          {sys.nominal_energy_cap ? ` / ${sys.nominal_energy_cap}kWh` : ''}
                        </p>
                      </div>
                      {existingMapping && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded flex items-center">
                          <CheckCircle className="w-3 h-3 mr-1" /> Mapped
                        </span>
                      )}
                    </div>

                    {existingMapping ? (
                      <div className="mt-2 flex items-center justify-between text-xs text-green-700">
                        <span>Mapped to asset: {existingMapping.asset_id?.slice(0, 12)}...</span>
                        <div className="flex items-center space-x-2">
                          <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => onSyncNow(existingMapping.asset_id)}
                            disabled={jemPolling[existingMapping.asset_id]} data-testid={`jem-syncnow-${sysId}`}>
                            <Zap className={`w-3 h-3 mr-1 ${jemPolling[existingMapping.asset_id] ? 'animate-pulse' : ''}`} /> Sync Now
                          </Button>
                          <Button size="sm" variant="outline" className="text-xs h-7 text-red-500" onClick={() => onDeleteMapping(existingMapping.mapping_id)}
                            data-testid={`jem-unmap-${sysId}`}>
                            <Unlink className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-3 flex items-end gap-2">
                        <div className="flex-1">
                          <Label className="text-[10px]">Map to existing asset</Label>
                          <select value={assetSelect} onChange={e => setSelectedAssetForSystem(prev => ({ ...prev, [sysId]: e.target.value }))}
                            className="w-full mt-0.5 px-2 py-1.5 border border-slate-200 rounded text-xs">
                            <option value="">Select asset...</option>
                            {assets.filter(a => !jemMappings.some(m => m.asset_id === a.asset_id)).map(a => (
                              <option key={a.asset_id} value={a.asset_id}>{a.asset_name} ({a.installed_power_kw}kW)</option>
                            ))}
                          </select>
                        </div>
                        <Button size="sm" className="text-xs h-7 bg-slate-900" disabled={!assetSelect}
                          onClick={() => onMapSystem({ ...sys, station_id: linkedStation.station_id, system_id: sysId }, assetSelect)}
                          data-testid={`jem-map-${sysId}`}>
                          <Link className="w-3 h-3 mr-1" /> Map
                        </Button>
                        <Button size="sm" variant="outline" className="text-xs h-7"
                          onClick={() => onCreateAssetFromSystem({ ...sys, station_id: linkedStation.station_id, system_id: sysId })}
                          data-testid={`jem-create-asset-${sysId}`}>
                          <Plus className="w-3 h-3 mr-1" /> New Asset
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Real-time Telemetry Panel */}
      {jemMappings.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-sm p-6">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center">
            <Zap className="w-5 h-5 mr-2 text-amber-500" /> Last Poll — Real-time Data
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {jemMappings.map(m => {
              const td = jemTelemetry[m.asset_id];
              const t = td?.telemetry;
              const ps = td?.poll_state;
              const asset = assets.find(a => a.asset_id === m.asset_id);
              return (
                <div key={m.mapping_id} className="border border-slate-100 rounded p-4" data-testid={`jem-telemetry-${m.asset_id}`}>
                  <div className="flex items-center justify-between mb-3">
                    <p className="font-medium text-sm text-slate-900">{asset?.asset_name || m.asset_id?.slice(0, 12)}</p>
                    <div className="flex items-center space-x-2">
                      {ps?.last_poll_status === 'success' && <CheckCircle className="w-4 h-4 text-green-500" />}
                      {ps?.last_poll_status === 'error' && <XCircle className="w-4 h-4 text-red-500" />}
                      {!ps?.last_poll_status && <Clock className="w-4 h-4 text-slate-300" />}
                      <Button size="sm" variant="outline" className="text-xs h-6" onClick={() => onSyncNow(m.asset_id)}
                        disabled={jemPolling[m.asset_id]}>
                        <Zap className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                  {t ? (
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div>
                        <p className="text-slate-500">SoC</p>
                        <p className="text-lg font-mono font-bold text-slate-900">{t.soc_percent?.toFixed(1) || '—'}%</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Charge / Discharge</p>
                        <p className="text-sm font-mono text-slate-900">
                          {t.charge_power_kw?.toFixed(1) || '0'} / {t.discharge_power_kw?.toFixed(1) || '0'} kW
                        </p>
                      </div>
                      <div>
                        <p className="text-slate-500">Cum. Charge</p>
                        <p className="text-sm font-mono text-slate-900">{t.cumulative_charge_kwh?.toFixed(0) || '—'} kWh</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Cum. Discharge</p>
                        <p className="text-sm font-mono text-slate-900">{t.cumulative_discharge_kwh?.toFixed(0) || '—'} kWh</p>
                      </div>
                      <div className="col-span-2">
                        <p className="text-[10px] text-slate-400">
                          Polled: {ps?.last_poll_ts ? new Date(ps.last_poll_ts).toLocaleString() : 'Never'}
                          {ps?.last_error && <span className="text-red-500 ml-2">Error: {ps.last_error}</span>}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-slate-400">No telemetry data yet. Click sync to poll.</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
